'use client'

import { useState } from 'react'
import TabbedNavigation from './TabbedNavigation'
import { formatFontFamily } from '@/lib/themeHelpers'
import { 
  Monitor, 
  ShareNetwork,
  Television, 
  Microphone, 
  Calendar 
} from 'phosphor-react'

interface InventorySectionProps {
  surface: "dark" | "light";
  theme: any;
  radius: number;
  fontFamily: string;
  gradStart: string;
  gradEnd: string;
  angle: number;
  content: any;
}

export default function InventorySection({ 
  surface, 
  theme, 
  radius, 
  fontFamily, 
  gradStart, 
  gradEnd, 
  angle,
  content
}: InventorySectionProps) {
  const [activeTab, setActiveTab] = useState('radio');

  const backgroundStyle = {
    backgroundImage: surface === "dark" 
      ? `linear-gradient(${angle}deg, ${gradStart}, ${gradEnd})`
      : `linear-gradient(${angle}deg, #f8f9fa, #e9ecef)`,
    fontFamily: formatFontFamily(fontFamily)
  };

  // Icon definitions using Phosphor icons - Radio/WVON appropriate
  const iconMap = {
    radio: <Microphone size={16} weight="duotone" />,
    vontv: <Television size={16} weight="duotone" />,
    streaming: <Television size={16} weight="duotone" />,
    digital: <Monitor size={16} weight="duotone" />,
    social: <ShareNetwork size={16} weight="duotone" />,
    events: <Calendar size={16} weight="duotone" />
  };

  // Get tabs from content or use default, filter for active tabs only
  const inventoryTabs = content.tabs || [];
  const activeTabs = inventoryTabs.filter((tab: any) => tab.isActive !== false);
  const tabs = activeTabs.map((tab: any) => ({
    id: tab.id,
    label: tab.label,
    icon: iconMap[tab.id as keyof typeof iconMap] || iconMap.radio
  }));

  // Set default active tab if current one doesn't exist
  if (tabs.length > 0 && !tabs.find((tab: any) => tab.id === activeTab)) {
    setActiveTab(tabs[0].id);
  }

  // Get current tab content from dynamic tabs
  const currentTab = activeTabs.find((tab: any) => tab.id === activeTab);
  const currentContent = currentTab ? {
    title: currentTab.title,
    description: currentTab.description,
    stats: currentTab.stats.map((stat: any) => ({ key: stat.value, value: stat.label })),
  } : {
    title: 'No Content',
    description: 'No inventory tabs available. Please add some tabs in the editor.',
    stats: [],
  };

  return (
    <section id="inventory" className="relative w-full py-16 bg-white" style={backgroundStyle}>
      <div className="mx-auto max-w-6xl px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold mb-4 font-primary" style={{ color: theme.text }}>
            {content.title}
          </h2>
          <p className="text-lg max-w-3xl mx-auto font-primary" style={{ color: theme.mutedText }}>
            {content.description}
          </p>
        </div>

        {/* Tabbed Navigation */}
        <TabbedNavigation 
          tabs={tabs}
          defaultActiveTab={activeTab}
          onTabChange={setActiveTab}
          theme={theme}
          radius={radius}
        />

        {/* Content Area - 2 Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-12">
          {/* Left Column - Content Container */}
          <div className="p-8 shadow-sm" style={{
            backgroundColor: theme.card,
            border: `1px solid ${theme.border}`,
            borderRadius: radius
          }}>
            {/* Header Section */}
            <div className="mb-8">
              <h3 className="text-2xl font-bold mb-4 font-primary" style={{ color: theme.text }}>
                {currentContent.title}
              </h3>
              <p className="text-base leading-relaxed font-primary" style={{ color: theme.mutedText }}>
                {currentContent.description}
              </p>
            </div>

            {/* Highlighted Items */}
            <div className="space-y-4">
              {currentContent.stats.map((stat: any, index: number) => (
                <div 
                  key={index}
                  className="flex justify-between items-start py-4"
                  style={{
                    borderBottom: index < currentContent.stats.length - 1 ? `1px solid ${theme.border}` : 'none'
                  }}
                >
                  <div className="font-semibold text-sm font-secondary" style={{ color: theme.text }}>
                    {stat.key}
                  </div>
                  <div className="text-sm text-right ml-4 font-secondary" style={{ color: theme.mutedText }}>
                    {stat.value}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Column - Image */}
          <div className="flex items-center justify-center">
            <div className="w-full h-96 rounded-lg overflow-hidden shadow-lg" style={{ minHeight: '400px' }}>
              <img 
                src={`/images/${activeTab}.png`}
                alt={`${currentContent.title} illustration`}
                className="w-full h-full object-cover"
                onError={(e) => {
                  // Fallback to placeholder if image doesn't exist
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  const parent = target.parentElement;
                  if (parent) {
                    parent.innerHTML = `
                      <div class="w-full h-full flex items-center justify-center border-2 border-dashed" style="background-color: ${theme.input}; border-color: ${theme.border};">
                        <div class="text-center">
                          <div class="text-4xl mb-2" style="color: ${theme.mutedText}">📸</div>
                          <p class="text-sm font-primary" style="color: ${theme.mutedText}">${currentContent.title} Image</p>
                        </div>
                      </div>
                    `;
                  }
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
